import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '../Header.js';
import Sidebar from '../Sidebar.js';
import { requestAppPermissions } from '../../../utils/devicePermissions.js';

const DashboardChromeContext = createContext(null);

function DashboardChromeProvider({ children }) {
  const [pageLayout, setPageLayoutState] = useState({ fullWidth: false, fillViewport: false });
  const setPageLayout = useCallback((next) => {
    setPageLayoutState((prev) => {
      if (prev.fullWidth === next.fullWidth && prev.fillViewport === next.fillViewport) {
        return prev;
      }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ pageLayout, setPageLayout }), [pageLayout, setPageLayout]);
  return (
    <DashboardChromeContext.Provider value={value}>
      {children}
    </DashboardChromeContext.Provider>
  );
}

function DashboardShell({ children }) {
  const { pageLayout } = useContext(DashboardChromeContext);
  const { fullWidth, fillViewport } = pageLayout;

  useEffect(() => {
    requestAppPermissions();
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header />
      <div className="relative z-0 flex flex-1 min-h-0 overflow-hidden bg-white">
        <Sidebar />
        <p className="pointer-events-none absolute bottom-2 right-3 z-10 select-none font-serif text-sm font-normal tracking-normal text-black sm:bottom-3 sm:right-5 sm:text-base" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
          Powered by DigiQin
        </p>
        <main
          className={`flex-1 min-w-0 min-h-0 overflow-x-hidden ${
            fillViewport
              ? 'px-2 pt-2 pb-2 sm:px-3 sm:pb-3 md:px-4 md:pb-4 overflow-hidden flex flex-col'
              : 'px-3 pt-2 pb-8 sm:px-4 sm:pb-9 md:px-6 md:pt-2.5 md:pb-10 lg:px-8 lg:pt-3 lg:pb-10 overflow-y-auto'
          }`}
        >
          <div className={`${fullWidth ? 'w-full' : 'max-w-[1600px] mx-auto'} ${fillViewport ? 'flex-1 min-h-0 flex flex-col min-w-0' : ''}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * App chrome (header + sidebar). Used as a persistent route layout so navigating
 * between pages does not remount / flash the header. Nested page-level
 * <DashboardLayout> wrappers only pass fullWidth / fillViewport through.
 */
function DashboardLayout({ children, fullWidth = false, fillViewport = false }) {
  const chrome = useContext(DashboardChromeContext);
  // setPageLayout is stable (useCallback []); do not depend on `chrome` itself —
  // it changes whenever pageLayout updates and would re-fire this effect forever
  // for pages with fullWidth / fillViewport (e.g. UserList).
  const setPageLayout = chrome?.setPageLayout;
  const content = children != null ? children : <Outlet />;

  useLayoutEffect(() => {
    if (!setPageLayout) return undefined;
    setPageLayout({ fullWidth, fillViewport });
    return () => setPageLayout({ fullWidth: false, fillViewport: false });
  }, [setPageLayout, fullWidth, fillViewport]);

  if (chrome) {
    return content;
  }

  return (
    <DashboardChromeProvider>
      <DashboardShell>{content}</DashboardShell>
    </DashboardChromeProvider>
  );
}

export default DashboardLayout;
